import { z } from 'zod';

import { SaleFormInput, FormInputInputType } from '@src/core/buyerApiClient';
import { parsePhoneNumber } from '@src/common/phoneParsing';
import { IDENTITY_TYPE, isValidBrazilianDocument, normalizeIdentity } from '@src/domains/customers';
import { isValidFormInputDisplayDate } from '@src/domains/orders';
import { requiredEmailFieldSchema } from '@src/features/customers/auth';
import type { Translate } from '@src/i18n/useTranslate';

/** Values of one tariff form, indexed by the input `hash`. */
export type TariffFormValues = Record<string, string>;

type TariffFormInputsSchemaInput = {
  inputs: SaleFormInput[];
  t: Translate['i18n'];
};

const BRAZIL_ALPHA2 = 'BR';

/**
 * Only the rules the code already knows get client-side validation; any other falls
 * back to required, and the Core stays the final word through the 422.
 *
 * Each check reuses the validator the rest of the checkout already relies on, so a
 * document or phone rejected here is rejected the same way at signup and at payment.
 */
const RULE_CHECKS: Record<string, { check: (value: string) => boolean; messageKey: string }> = {
  cpf_format: {
    check: (value) => isValidBrazilianDocument(normalizeIdentity(value), IDENTITY_TYPE.CPF),
    messageKey: 'checkout_additional_info_invalid_cpf',
  },
  phone_format: {
    check: (value) => parsePhoneNumber(value, BRAZIL_ALPHA2).isValid,
    messageKey: 'checkout_additional_info_invalid_phone',
  },
};

export const tariffFormInputsSchema = ({ inputs, t }: TariffFormInputsSchemaInput) => {
  const requiredMessage = t('checkout_auth_required_field');

  const shape = inputs.reduce<Record<string, z.ZodType<string>>>((acc, input) => {
    // Sellers pair rules (`numeric` + `cpf_format`), so the one that counts is the first this form knows.
    const rule = (input.validationRules ?? []).find((candidate) => candidate === 'email' || candidate in RULE_CHECKS);

    if (rule === 'email') {
      acc[input.hash] = requiredEmailFieldSchema(t);

      return acc;
    }

    const field = z
      .string({ required_error: requiredMessage })
      .refine((value) => value.trim() !== '', { message: requiredMessage });

    if (input.inputType === FormInputInputType.Date) {
      acc[input.hash] = field.refine(isValidFormInputDisplayDate, {
        message: t('checkout_additional_info_invalid_date'),
      });

      return acc;
    }

    const validator = rule ? RULE_CHECKS[rule] : undefined;

    acc[input.hash] = validator
      ? field.refine(validator.check, { message: t(validator.messageKey as Parameters<Translate['i18n']>[0]) })
      : field;

    return acc;
  }, {});

  return z.object(shape);
};
